import { trace } from '@opentelemetry/api';
import { getConfig, serviceBaseUrls, type RuntimeConfig } from './config';

export interface ApiResponse<T> {
  data: T;
  traceId: string | null;
  status: number;
  elapsedMs: number;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public traceId: string | null,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Defaults to true — every cross-service call needs the session cookie. */
  credentials?: RequestCredentials;
}

function activeTraceId(): string | null {
  const span = trace.getActiveSpan();
  if (!span) return null;
  const ctx = span.spanContext();
  return ctx?.traceId ?? null;
}

async function request<T>(
  url: string,
  opts: RequestOpts = {},
): Promise<ApiResponse<T>> {
  const start = performance.now();
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: opts.credentials ?? 'include', // session cookie!
    headers: {
      'Accept': 'application/json',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.headers ?? {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  };
  const res = await fetch(url, init);
  const elapsedMs = performance.now() - start;
  const traceId = activeTraceId();
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!res.ok) {
    // FastAPI's hand-raised HTTPExceptions (e.g. the instructor toggle's
    // range check) put the useful text in a string `detail` — that's the
    // only feedback an instructor gets when a slider or a curl asks for
    // something the stack won't do, so it belongs in the toast, not buried
    // in `.body`. FastAPI's OWN request-validation 422s put a list of dicts
    // in `detail` instead; only the string case is safe to surface directly.
    const parsedDetail =
      body && typeof body === 'object' && 'detail' in (body as Record<string, unknown>)
        ? (body as Record<string, unknown>).detail
        : null;
    // Keep the message to just the detail text when we have one — a toast
    // is one line with truncation, and `${method} ${url} -> ${status}`
    // tacked on the end is exactly what pushes the useful part off-screen.
    // `.status` and `.body` still carry the method/url/status information
    // for anyone who needs it.
    const message =
      typeof parsedDetail === 'string' && parsedDetail
        ? parsedDetail
        : `${init.method} ${url} -> ${res.status}`;
    throw new ApiError(res.status, traceId, message, body);
  }
  return { data: body as T, traceId, status: res.status, elapsedMs };
}

// ---- Domain types ----

export interface Product {
  id: string;
  sku: string;
  name: string;
  category: string;
  description: string;
  price_knuts: number;
  image_url: string;
  stock: number;
}

export interface Category {
  name: string;
  slug: string;
  count?: number;
}

export interface CartItem {
  productId: string;
  quantity: number;
  addedAt?: string;
  // hydrated client-side from catalog
  product?: Product;
}

export interface Cart {
  sessionId: string;
  items: CartItem[];
}

export interface User {
  id: string;
  email: string;
}

export interface OrderItem {
  productId: string;
  sku: string;
  name: string;
  quantity: number;
  unit_price_knuts: number;
}

export interface Order {
  id: string;
  user_id: string;
  status: 'placed' | 'fulfilled' | 'failed';
  total_knuts: number;
  items: OrderItem[];
  payment_outcome: string;
  placed_at: string;
  fulfilled_at: string | null;
}

export interface CheckoutPreview {
  items: OrderItem[];
  total_knuts: number;
}

// Shape produced by catalog `/api/instructor/alert-notification` (A1).
// Fields beyond `name` are best-effort because A1 was authored before B2 and
// only required `{name, severity?}`. B2's webhook handler (still A1's file)
// populates the richer record below — we type it here so every caller shares
// one contract instead of re-declaring it next to the component that reads it.
export interface ActiveAlert {
  uid?: string;
  name?: string; // legacy field from A1 — kept for back-compat
  title?: string;
  summary?: string;
  severity?: string;
  runbookUrl?: string;
  // Multi-line plain text (may look like markdown) from the alert rule's
  // `runbook` annotation. Absent or "" on rules that don't set one.
  runbook?: string;
  firedAt?: string;
  status?: string;
}

// ---- Clients ----

function urls(): ReturnType<typeof serviceBaseUrls> {
  return serviceBaseUrls(getConfig());
}

// Backend list-shaped catalog endpoints wrap arrays in `{products: [...]}` /
// `{categories: [...]}` so a future schema (e.g. adding `total`) doesn't
// break the JSON contract. Unwrap here so callers see flat arrays.
async function unwrap<W, K extends keyof W>(
  p: Promise<ApiResponse<W>>,
  key: K,
): Promise<ApiResponse<W[K]>> {
  const res = await p;
  return { ...res, data: (res.data as W)[key] };
}

export const catalogApi = {
  list(params: { category?: string; q?: string; limit?: number; offset?: number } = {}) {
    const u = new URL(`${urls().catalog}/api/catalog/products`);
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') u.searchParams.set(k, String(v));
    });
    return unwrap(
      request<{ products: Product[]; total: number }>(u.toString()),
      'products',
    );
  },
  get(id: string) {
    return request<Product>(`${urls().catalog}/api/catalog/products/${encodeURIComponent(id)}`);
  },
  categories() {
    return unwrap(
      request<{ categories: Category[] }>(`${urls().catalog}/api/catalog/categories`),
      'categories',
    );
  },
  featured() {
    return unwrap(
      request<{ products: Product[] }>(`${urls().catalog}/api/catalog/featured`),
      'products',
    );
  },
};

// Cart-mutation endpoints return { productId, quantity, cart: {items, total} }
// (not the bare Cart). GET /api/cart returns the Cart directly.
async function unwrapCart(
  p: Promise<ApiResponse<{ cart: Cart } | Cart>>,
): Promise<ApiResponse<Cart>> {
  const res = await p;
  const data = res.data as { cart?: Cart } & Cart;
  return { ...res, data: data.cart ?? (data as Cart) };
}

export const cartApi = {
  get() {
    return request<Cart>(`${urls().cart}/api/cart`);
  },
  add(productId: string, quantity = 1) {
    return unwrapCart(
      request<{ cart: Cart }>(`${urls().cart}/api/cart/items`, {
        method: 'POST',
        body: { productId, quantity },
      }),
    );
  },
  update(productId: string, quantity: number) {
    return unwrapCart(
      request<{ cart: Cart }>(`${urls().cart}/api/cart/items/${encodeURIComponent(productId)}`, {
        method: 'PATCH',
        body: { quantity },
      }),
    );
  },
  remove(productId: string) {
    return unwrapCart(
      request<{ cart: Cart }>(`${urls().cart}/api/cart/items/${encodeURIComponent(productId)}`, {
        method: 'DELETE',
      }),
    );
  },
  clear() {
    return request<Cart>(`${urls().cart}/api/cart`, { method: 'DELETE' });
  },
};

// Checkout `place` returns { orderId, status, payment_outcome, total_knuts }
// — a placement summary, NOT a full Order. Normalize to the Order shape so
// route consumers can use a single type.
type PlaceResult = {
  orderId: string;
  status: Order['status'];
  payment_outcome: string;
  total_knuts: number;
};

// `orders` list returns { orders: [{ orderId, ...}] }; per-order GET likely same shape.
type WireOrder = Omit<Order, 'id'> & { orderId: string };
function normalizeOrder(o: WireOrder): Order {
  return { ...o, id: o.orderId };
}

export const checkoutApi = {
  preview(items: Array<{ productId: string; quantity: number }>) {
    return request<CheckoutPreview>(`${urls().checkout}/api/checkout/preview`, {
      method: 'POST',
      body: { items },
    });
  },
  async place() {
    const res = await request<PlaceResult>(`${urls().checkout}/api/checkout/place`, { method: 'POST' });
    // Backfill the fields a full Order has but place() doesn't return.
    const o: Order = {
      id: res.data.orderId,
      user_id: '',
      status: res.data.status,
      total_knuts: res.data.total_knuts,
      items: [],
      payment_outcome: res.data.payment_outcome,
      placed_at: new Date().toISOString(),
      fulfilled_at: null,
    };
    return { ...res, data: o };
  },
  async orders() {
    const res = await request<{ orders: WireOrder[] }>(`${urls().checkout}/api/checkout/orders`);
    return { ...res, data: res.data.orders.map(normalizeOrder) };
  },
  async order(id: string) {
    const res = await request<WireOrder>(
      `${urls().checkout}/api/checkout/orders/${encodeURIComponent(id)}`,
    );
    return { ...res, data: normalizeOrder(res.data) };
  },
};

export const authApi = {
  async me() {
    const res = await request<{ user: User } | null>(`${urls().catalog}/api/auth/me`);
    return { ...res, data: res.data?.user ?? null };
  },
  async login(email: string) {
    const res = await request<{ user: User }>(`${urls().catalog}/api/auth/login`, {
      method: 'POST',
      body: { email },
    });
    return { ...res, data: res.data.user };
  },
  logout() {
    return request<{ ok: true }>(`${urls().catalog}/api/auth/logout`, {
      method: 'POST',
    });
  },
};

export const instructorApi = {
  // Backend returns { state: { key: value, ... } } so a future contract bump
  // can add sibling fields. Unwrap so the Instructor page can index by toggle key.
  async state(token: string) {
    const res = await request<{ state: Record<string, unknown> }>(
      `${urls().instructor}/api/instructor/state`,
      { headers: { 'X-Instructor-Token': token } },
    );
    return { ...res, data: res.data.state ?? {} };
  },
  toggle(token: string, key: string, value: unknown) {
    return request<{ ok: true; state: Record<string, unknown> }>(
      `${urls().instructor}/api/instructor/toggle`,
      {
        method: 'POST',
        body: { key, value },
        headers: { 'X-Instructor-Token': token },
      },
    );
  },
  reset(token: string) {
    return request<{ ok: true }>(
      `${urls().instructor}/api/instructor/reset`,
      { method: 'POST', headers: { 'X-Instructor-Token': token } },
    );
  },
  // Public alert ticker per A1-DONE; no auth required.
  alerts(): Promise<ApiResponse<{ active: ActiveAlert[] }>> {
    return request(`${urls().instructor}/api/instructor/alerts`);
  },
};

// Exported for tests.
export const __test = { request, activeTraceId };
export type { RuntimeConfig };
